import { z } from 'zod';
import {
  FOOTBALL_LEVELS,
  GAME_STATUSES,
  LEAGUE_ROLES,
  MEMBER_STATUSES,
  POOL_MODES,
  REVEAL_POLICIES,
  SIDES,
  SLATE_EVENT_TYPES,
} from '../constants';
import type {
  FootballLevel,
  GameStatus,
  LeagueRole,
  MemberStatus,
  SlateEventType,
} from '../constants';
import { decisiveSpreadValue, isoDateTime, teamScore } from './common';
import type { TeamDto } from './week';

// ---------------------------------------------------------------------------
// Eligible schedule browsing
// ---------------------------------------------------------------------------

export const eligibleGamesQuery = z.object({
  level: z.enum(FOOTBALL_LEVELS).optional(),
  /** Free-text match on team name, nickname, abbreviation or location. */
  q: z.string().trim().max(64).optional(),
  conference: z.string().trim().max(64).optional(),
  /** `YYYY-MM-DD` in the league's display timezone. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Include games already on this week's slate, flagged rather than hidden. */
  includeSelected: z.coerce.boolean().default(false),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type EligibleGamesQuery = z.infer<typeof eligibleGamesQuery>;

/**
 * A row in the commissioner's schedule browser. `eligible` and `ineligibleReasons` are
 * computed server-side so the browser can show a greyed-out game *with the reason*
 * rather than silently omitting it — "where did that game go" is a worse experience than
 * "that game already started".
 */
export interface ScheduleGameDto {
  id: string;
  level: FootballLevel;
  seasonYear: number;
  weekNumber: number;
  kickoffAt: string;
  neutralSite: boolean;
  venue: string | null;
  network: string | null;
  status: GameStatus;
  homeScore: number | null;
  awayScore: number | null;
  marketSpreadHome: number | null;
  lineUpdatedAt: string | null;
  home: TeamDto;
  away: TeamDto;
  eligible: boolean;
  ineligibleReasons: EligibilityReason[];
  /** True when this game is already on the week's slate. */
  onSlate: boolean;
}

export type EligibilityReason =
  | 'already_on_slate'
  | 'wrong_week'
  | 'outside_window'
  | 'kickoff_too_soon'
  | 'not_scheduled'
  | 'no_spread'
  | 'wrong_level';

// ---------------------------------------------------------------------------
// Slate operations
// ---------------------------------------------------------------------------

export const generateSlateBody = z
  .object({
    /** Fill only empty slots instead of requiring an empty slate. Defaults to true. */
    fillOnly: z.boolean().default(true),
  })
  .default({ fillOnly: true });
export type GenerateSlateBody = z.infer<typeof generateSlateBody>;

export const rerollAllBody = z
  .object({
    /**
     * Exclude the games currently on the slate so a reroll is genuinely a new five.
     * Falls back automatically (and reports it) when the pool is too small to honour this.
     */
    excludeCurrent: z.boolean().default(true),
  })
  .default({ excludeCurrent: true });
export type RerollAllBody = z.infer<typeof rerollAllBody>;

export const addSlateGameBody = z.object({
  scheduleGameId: z.string().uuid(),
  slot: z.number().int().min(1).max(20).optional(),
  /** Override the market line when adding. Defaults to the market line, hooked. */
  contestSpreadHome: decisiveSpreadValue.optional(),
});
export type AddSlateGameBody = z.infer<typeof addSlateGameBody>;

/**
 * Editing a published slate game. `acknowledgePicksAffected` is the deliberate friction:
 * the commissioner must have seen the count of existing picks before the write is allowed.
 * Without it the server returns ACKNOWLEDGEMENT_REQUIRED *with* that count, so the UI can
 * show a real confirmation instead of a generic "are you sure".
 */
export const updateSlateGameBody = z
  .object({
    scheduleGameId: z.string().uuid().optional(),
    contestSpreadHome: decisiveSpreadValue.optional(),
    contestKickoffAt: isoDateTime.optional(),
    acknowledgePicksAffected: z.boolean().default(false),
    reason: z.string().trim().max(280).optional(),
  })
  .refine(
    (v) =>
      v.scheduleGameId !== undefined ||
      v.contestSpreadHome !== undefined ||
      v.contestKickoffAt !== undefined,
    { message: 'Nothing to update' },
  );
export type UpdateSlateGameBody = z.infer<typeof updateSlateGameBody>;

export const removeSlateGameQuery = z.object({
  /** Required once the slate is published, because removal becomes a void. */
  reason: z.string().trim().max(280).optional(),
  acknowledgePicksAffected: z.coerce.boolean().default(false),
});
export type RemoveSlateGameQuery = z.infer<typeof removeSlateGameQuery>;

export const reorderSlateBody = z.object({
  slateGameIds: z.array(z.string().uuid()).min(1).max(20),
});
export type ReorderSlateBody = z.infer<typeof reorderSlateBody>;

export const publishSlateBody = z
  .object({
    /** Publish even though a kickoff has already passed (that game locks immediately). */
    allowPastKickoffs: z.boolean().default(false),
  })
  .default({ allowPastKickoffs: false });
export type PublishSlateBody = z.infer<typeof publishSlateBody>;

export const unpublishSlateQuery = z.object({
  /** Required when picks already exist. Picks are retained either way. */
  force: z.coerce.boolean().default(false),
});
export type UnpublishSlateQuery = z.infer<typeof unpublishSlateQuery>;

/**
 * Returned by generate / reroll so the UI can narrate what happened — "3 NFL from 14
 * eligible, 2 CFB from 41" is far more reassuring than a silent list of five games.
 */
export interface SlateGenerationReportDto {
  weekId: string;
  drawn: number;
  buckets: {
    level: FootballLevel;
    requested: number;
    eligible: number;
    drawn: number;
  }[];
  /** True when the pool was too small to honour the exclusion list and it was relaxed. */
  exclusionsRelaxed: boolean;
  replacedSlateGameIds: string[];
}

export interface InsufficientGamesDetail {
  level: FootballLevel | 'any';
  needed: number;
  available: number;
}

// ---------------------------------------------------------------------------
// Results and grading
// ---------------------------------------------------------------------------

export const setResultBody = z.object({
  homeScore: teamScore,
  awayScore: teamScore,
  status: z.enum(GAME_STATUSES).default('final'),
});
export type SetResultBody = z.infer<typeof setResultBody>;

/**
 * Manual grade override. Used when the scores are right but the outcome is not — a
 * disputed line, a corrected final, a game that has to be graded by fiat. Setting this
 * marks the game `gradeOverride`, which makes the provider sync leave it alone forever.
 */
export const overrideGradeBody = z.object({
  result: z.enum(['pending', 'home_covered', 'away_covered', 'push', 'void']),
  reason: z.string().trim().min(1, 'Give a reason so this is auditable').max(280),
});
export type OverrideGradeBody = z.infer<typeof overrideGradeBody>;

export const overridePickBody = z.object({
  selection: z.enum(SIDES).nullable(),
  reason: z.string().trim().min(1, 'Give a reason so this is auditable').max(280),
});
export type OverridePickBody = z.infer<typeof overridePickBody>;

// ---------------------------------------------------------------------------
// Members, invites, season config
// ---------------------------------------------------------------------------

export const createInviteBody = z.object({
  role: z.enum(LEAGUE_ROLES).default('player'),
  maxUses: z.number().int().min(1).max(500).nullable().default(null),
  expiresAt: isoDateTime.nullable().default(null),
  note: z.string().trim().max(120).optional(),
});
export type CreateInviteBody = z.infer<typeof createInviteBody>;

export const updateMemberBody = z
  .object({
    role: z.enum(LEAGUE_ROLES).optional(),
    status: z.enum(MEMBER_STATUSES).optional(),
    rosterAccess: z.boolean().optional(),
  })
  .refine(
    (v) => v.role !== undefined || v.status !== undefined || v.rosterAccess !== undefined,
    { message: 'Nothing to update' },
  );
export type UpdateMemberBody = z.infer<typeof updateMemberBody>;

export const updateSeasonConfigBody = z
  .object({
    name: z.string().trim().min(1).max(64).optional(),
    gamesPerWeek: z.number().int().min(1).max(20).optional(),
    poolMode: z.enum(POOL_MODES).optional(),
    mixedNflCount: z.number().int().min(0).max(20).nullable().optional(),
    mixedCfbCount: z.number().int().min(0).max(20).nullable().optional(),
    winPoints: z.number().min(0).max(100).optional(),
    lossPoints: z.number().min(0).max(100).optional(),
    pushPoints: z.number().min(0).max(100).optional(),
    revealPolicy: z.enum(REVEAL_POLICIES).optional(),
    displayTimezone: z.string().trim().max(64).optional(),
  })
  .refine(
    (v) =>
      v.poolMode !== 'mixed_fixed' ||
      (v.mixedNflCount !== null &&
        v.mixedNflCount !== undefined &&
        v.mixedCfbCount !== null &&
        v.mixedCfbCount !== undefined),
    {
      message: 'A fixed mix needs both an NFL count and a CFB count',
      path: ['mixedNflCount'],
    },
  );
export type UpdateSeasonConfigBody = z.infer<typeof updateSeasonConfigBody>;

export interface LeagueMemberDto {
  membershipId: string;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  email: string;
  role: LeagueRole;
  status: MemberStatus;
  /** Whether this member can open the roster page. Commissioners always can. */
  rosterAccess: boolean;
  joinedAt: string;
  isMe: boolean;
  /** Season-to-date, so the members table doubles as a participation check. */
  picksMade: number;
  picksAvailable: number;
  points: number;
}

/**
 * A one-time password-reset link the commissioner can hand to a member.
 *
 * Deliberately not a password. The commissioner helps somebody back in without ever
 * knowing what they choose — which matters more than it looks, because people reuse
 * passwords and "my league admin picked my password" is a bad place for that to start.
 */
export interface ResetLinkDto {
  userId: string;
  displayName: string;
  url: string;
  expiresAt: string;
}

export interface InviteDto {
  id: string;
  code: string;
  role: LeagueRole;
  maxUses: number | null;
  uses: number;
  expiresAt: string | null;
  revokedAt: string | null;
  note: string | null;
  createdAt: string;
  createdByName: string;
  active: boolean;
}

// ---------------------------------------------------------------------------
// Commissioner dashboard + audit
// ---------------------------------------------------------------------------

export interface CommishOverviewDto {
  week: {
    id: string;
    weekNumber: number;
    label: string;
    slateStatus: string;
    gameCount: number;
    requiredGames: number;
    firstKickoffAt: string | null;
    picksLockedOverride: boolean | null;
  } | null;
  /** Members who have completed every pick on the published slate. */
  playersComplete: number;
  playersPartial: number;
  playersNotStarted: number;
  memberCount: number;
  /** Games that are final but not yet graded — the commissioner's to-do list. */
  ungradedGames: number;
  /** Weeks with a draft slate, oldest first. */
  draftWeeks: { id: string; weekNumber: number; label: string; gameCount: number }[];
}

export interface SlateEventDto {
  id: string;
  weekId: string;
  slateGameId: string | null;
  actorName: string | null;
  eventType: SlateEventType;
  payload: Record<string, unknown>;
  createdAt: string;
}

export const slateEventTypeEnum = z.enum(SLATE_EVENT_TYPES);

// ---------------------------------------------------------------------------
// Provider sync
// ---------------------------------------------------------------------------

/**
 * Pulling the schedule and pulling the spreads are two separate operations, and the split
 * is the point.
 *
 * The schedule comes from ESPN and is free, so it can be refreshed as often as anyone
 * likes. Spreads come from a metered upstream where every call is spent permanently
 * against a monthly allowance. Bundling them would mean every routine schedule refresh
 * quietly cost a credit, which is exactly how an allowance disappears without anyone
 * deciding to spend it. So spreads are never a side effect: something has to ask for them.
 */
export const syncScheduleBody = z.object({
  level: z.enum(FOOTBALL_LEVELS),
  seasonYear: z.number().int().min(2000).max(2100),
  week: z.number().int().min(1).max(25).optional(),
});
export type SyncScheduleBody = z.infer<typeof syncScheduleBody>;

export const refreshOddsBody = z.object({
  /** Omit to price both pools. One credit per level either way. */
  level: z.enum(FOOTBALL_LEVELS).optional(),
  seasonYear: z.number().int().min(2000).max(2100),
  /** Spend a credit even though the stored lines are still inside the freshness window. */
  force: z.boolean().default(false),
});
export type RefreshOddsBody = z.infer<typeof refreshOddsBody>;

/** What a metered upstream says about our remaining budget. */
export interface ProviderQuotaDto {
  provider: string;
  remaining: number | null;
  used: number | null;
  lastCost: number | null;
  lastRequestAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  refusedCount: number;
  /** Calls are refused at or below this many remaining credits. */
  reserve: number;
  canSpend: boolean;
}

export interface SyncReportDto {
  provider: string;
  teamsUpserted: number;
  gamesUpserted: number;
  linesUpdated: number;
  scoresUpdated: number;
  /** Games auto-graded as a result of this sync. Never includes overridden games. */
  gamesGraded: number;
  warnings: string[];
}

/** One level's worth of spread refresh. */
export interface OddsLevelReportDto {
  level: FootballLevel;
  /** Games whose market line moved as a result. */
  linesUpdated: number;
  /** True when the network was never touched — fresh lines, or no credits to spend. */
  skipped: boolean;
  /** Why it was skipped, in words meant for the commissioner. */
  note: string | null;
}

export interface OddsRefreshReportDto {
  levels: OddsLevelReportDto[];
  /** Credits actually consumed, read back from the upstream rather than assumed. */
  creditsSpent: number;
  quota: ProviderQuotaDto;
  warnings: string[];
}

/**
 * The outcome of settling a week from the scoreboard feed.
 *
 * Reports both halves separately because they fail independently: the feed can be
 * unreachable (`gamesUpdated` 0, a warning) while grading still settles games whose scores
 * arrived earlier, and a healthy feed can update every score without settling anything if
 * the games are still being played.
 */
export interface SettleWeekReportDto {
  /** Games that moved from pending to a final result. Never includes overridden games. */
  graded: number;
  /** Schedule rows the feed refreshed — scores, clocks and statuses. */
  gamesUpdated: number;
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Broadcasts — the hand-written notification
// ---------------------------------------------------------------------------

/**
 * Every other notification this app sends is generated from league state. A broadcast is
 * the one a person wrote, which makes it the only one that can say something the rules
 * cannot produce — and the only one that can be wrong in a way no test would catch.
 *
 * Hence the shape: everything is editable while it is a draft, nothing is editable after
 * it has been sent, and the send is a separate call from every other verb.
 */
export const broadcastDraftBody = z.object({
  /** Notification title. Short — most launchers truncate around 40 characters. */
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(400),

  /** Small label above the landing headline: `BREAKING`, `LEAGUE NOTICE`. */
  landingKicker: z.string().trim().max(60).nullable().optional(),
  landingHeadline: z.string().trim().max(200).nullable().optional(),
  landingBody: z.string().trim().max(4000).nullable().optional(),

  /**
   * Where a tap goes. Null uses the composed landing page.
   *
   * Constrained to a path on this site rather than accepting a URL: a notification is the
   * one surface where the destination is invisible until it is too late, and an
   * open redirect there would be worth more to somebody else than to the commissioner.
   */
  targetPath: z
    .string()
    .trim()
    .max(200)
    .regex(/^\/(?!\/)[A-Za-z0-9\-._~!$&'()*+,;=:@%/?#[\]]*$/, 'Must be a path on this site, starting with /')
    .nullable()
    .optional(),
});
export type BroadcastDraftBody = z.infer<typeof broadcastDraftBody>;

export const broadcastImageBody = z.object({
  mimeType: z.enum(['image/webp', 'image/jpeg', 'image/png']),
  /** base64, no `data:` prefix. Capped so the decoded image stays under 1MB. */
  data: z.string().min(32).max(1_400_000),
});
export type BroadcastImageBody = z.infer<typeof broadcastImageBody>;

export interface BroadcastDto {
  id: string;
  title: string;
  body: string;
  landingKicker: string | null;
  landingHeadline: string | null;
  landingBody: string | null;
  /** Null means the composed landing page, whose path is `landingPath`. */
  targetPath: string | null;
  /** Where the composed landing page lives. Unguessable, and served without a session. */
  landingPath: string;
  /** Null when no image is attached. Absolute when `PUBLIC_API_URL` is set. */
  imageUrl: string | null;
  /** Null while it is still a draft. Set once, and it can never be edited again. */
  sentAt: string | null;
  deliveredCount: number | null;
  createdAt: string;
}

/** The result of actually sending one. */
export interface BroadcastSendReportDto {
  broadcast: BroadcastDto;
  /** Devices that accepted delivery. Zero is a real answer, not a failure. */
  delivered: number;
  /** People in the league with at least one live device. */
  recipients: number;
  note: string;
}

/**
 * The landing page, served to anyone holding the link — including someone not signed in.
 *
 * A notification that bounced its reader to a login screen would have failed at the only
 * job it had, so this is public. It carries no league data for the same reason.
 */
export interface BroadcastLandingDto {
  kicker: string | null;
  headline: string;
  body: string | null;
  imageUrl: string | null;
  sentAt: string | null;
}

// ---------------------------------------------------------------------------
// Roster
// ---------------------------------------------------------------------------

/**
 * One player on a college roster.
 *
 * Every field but the name is nullable, and that is the honest shape: this is merged from
 * two feeds that each know different things, and a roster where the missing pieces are
 * visible is more useful than one where they have been filled in with guesses.
 */
/** One leg of a transfer history. */
export interface RosterTransferDto {
  from: string;
  to: string;
  /** ISO instant the portal recorded the move. Null when it carried no date. */
  date: string | null;
  /** The portal's own wording: `Immediate`, `Waiver`. */
  eligibility: string | null;
}

export interface RosterPlayerDto {
  id: string;
  jersey: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  positionShort: string | null;
  positionName: string | null;
  height: string | null;
  weight: string | null;
  classShort: string | null;
  classLong: string | null;
  hometown: string | null;
  highSchool: string | null;
  /** Free text: `Oregon State / UCLA` for a player with two stops. Null means not a transfer. */
  previousSchool: string | null;
  /**
   * The move-by-move history, oldest first, when the transfer portal has one.
   *
   * Empty is not the same as `previousSchool` being null. The portal does not record every
   * move — a step through junior college or Division II often never appears — so a player
   * can have a previous school here and no dated history, which is why both survive.
   */
  transfers: RosterTransferDto[];
  /** Recruiting stars, 1 to 5. Null when neither feed rated this player. */
  stars: number | null;
  /** `recruit` is the high-school rating; `portal` is what the transfer portal assigned. */
  starSource: 'recruit' | 'portal' | null;
  headshotUrl: string | null;
  /** Second address for the same face, tried when the first fails to load. */
  altHeadshotUrl: string | null;
  profileUrl: string | null;
}

export interface RosterDto {
  seasonYear: number;
  players: RosterPlayerDto[];
  /** How many carry a previous school — the number this roster is interesting for. */
  transferCount: number;
  /** How many of those have a dated, move-by-move history rather than just school names. */
  datedTransferCount: number;
  /** How many players carry a star rating from either feed. */
  ratedCount: number;
  /** When the feeds were last read. Null when the roster has never been synced. */
  syncedAt: string | null;
}

export interface RosterSyncReportDto {
  seasonYear: number;
  playersUpserted: number;
  /** Players found on both feeds, which is what carries the transfer details. */
  matched: number;
  espnOnly: number;
  okstateOnly: number;
  transfersFound: number;
  /** Of those, how many the transfer portal could give a dated history for. */
  transferHistories: number;
  /** Players a star rating was found for. */
  ratingsFound: number;
  /** Empty when every feed was read cleanly. */
  warnings: string[];
}
